import { useTranslation } from 'react-i18next';
import { Receipt, Store } from 'lucide-react';
import type {
  AuthenticatedBranch,
  BasketCustomerIssue,
  SalesSettingsSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { useCurrency } from '@/hooks/useCurrency';
import type { Tender } from '@/modules/sales/api/sales.api';
import { CreditTermsPanel } from '@/modules/sales/components/CreditTermsPanel';
import { TenderPanel } from '@/modules/sales/components/TenderPanel';
import { EMPTY_CREDIT_TERMS, type CreditTermsDraft } from '@/modules/sales/creditTerms';
import type { BuilderProblem } from '../builder/refusals';
import { Figure } from './ReviewStep';

interface PaymentStepProps {
  totalMinor: number;
  tenders: Tender[];
  onTendersChange: (tenders: Tender[]) => void;
  credit: CreditTermsDraft | null;
  onCreditChange: (credit: CreditTermsDraft | null) => void;
  settings: SalesSettingsSummary | undefined;
  /** The server's answer on the customer's credit for the balance being financed. */
  creditIssues: BasketCustomerIssue[];
  showOverride: boolean;
  creditErrors: Record<string, string>;
  problems: BuilderProblem[];
  branch: AuthenticatedBranch | null;
  onConfirm: () => void;
  isConfirming: boolean;
  confirmDisabled: boolean;
}

/**
 * Step 5: the money, then the confirmation.
 *
 * The counter's own tender panel takes payment now — one method or several, the whole amount or a deposit. Whatever the tenders leave either stays owing on the order or, where the tenant gives credit, becomes a payment plan. The total is the server's; the order is taken at the branch named here.
 */
export function PaymentStep({
  totalMinor,
  tenders,
  onTendersChange,
  credit,
  onCreditChange,
  settings,
  creditIssues,
  showOverride,
  creditErrors,
  problems,
  branch,
  onConfirm,
  isConfirming,
  confirmDisabled,
}: PaymentStepProps) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const tendered = tenders.reduce((sum, tender) => sum + tender.amountMinor, 0);
  const remaining = Math.max(0, totalMinor - tendered);
  const creditAllowed = Boolean(settings?.allowCustomerCredit);

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
      <div className="min-w-0 space-y-5">
        <TenderPanel
          tenders={tenders}
          totalMinor={totalMinor}
          outstandingMinor={remaining}
          onChange={onTendersChange}
        />
        {tendered > totalMinor ? (
          <Alert tone="error">{t('opticalOrders.payment.overTendered')}</Alert>
        ) : null}

        {remaining > 0 ? (
          <Card>
            <CardHeader title={t('opticalOrders.payment.balanceTitle')} />
            <div className="space-y-3 px-5 py-4">
              {creditAllowed ? (
                <label className="text-ink flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={credit !== null}
                    onChange={(event) =>
                      onCreditChange(event.target.checked ? EMPTY_CREDIT_TERMS : null)
                    }
                  />
                  <span>{t('opticalOrders.payment.usePlan', { amount: format(remaining) })}</span>
                </label>
              ) : null}
              {credit ? (
                <CreditTermsPanel
                  value={credit}
                  onChange={onCreditChange}
                  financedMinor={remaining}
                  maxInstallments={settings?.maxInstallments ?? 12}
                  defaultCreditDays={settings?.defaultCreditDays ?? 30}
                  showOverride={showOverride}
                  errors={creditErrors}
                />
              ) : (
                <p className="text-muted text-sm">
                  {t('opticalOrders.payment.balanceOwing', { amount: format(remaining) })}
                </p>
              )}
              {credit && creditIssues.includes('CREDIT_UNAVAILABLE') ? (
                <Alert tone="error">{t('opticalOrders.credit.unavailable')}</Alert>
              ) : null}
              {credit && creditIssues.includes('CREDIT_LIMIT_EXCEEDED') ? (
                <Alert tone={settings?.creditLimitPolicy === 'WARN' ? 'warning' : 'error'}>
                  {t(
                    settings?.creditLimitPolicy === 'WARN'
                      ? 'opticalOrders.credit.limitWarning'
                      : 'opticalOrders.credit.limitExceeded',
                  )}
                </Alert>
              ) : null}
            </div>
          </Card>
        ) : null}
      </div>

      <div className="min-w-0 space-y-4">
        <Card>
          <CardHeader title={t('opticalOrders.payment.summary')} />
          <dl className="space-y-2 px-5 py-4">
            <Figure label={t('sales.fields.total')} value={format(totalMinor)} strong />
            <Figure label={t('opticalOrders.payment.payingNow')} value={format(tendered)} />
            <Figure
              label={
                credit ? t('opticalOrders.payment.onPlan') : t('opticalOrders.payment.leftOwing')
              }
              value={format(remaining)}
            />
          </dl>
        </Card>

        {branch ? (
          <p className="text-ink flex items-center gap-2 text-sm">
            <Store className="text-muted size-4 shrink-0" aria-hidden />
            <span>
              {t('opticalOrders.payment.takenAt', { branch: branch.name })}{' '}
              <span className="text-muted font-mono text-xs" dir="ltr">
                {branch.code}
              </span>
            </span>
          </p>
        ) : (
          <Alert tone="warning">{t('opticalOrders.builder.noBranch')}</Alert>
        )}

        {problems.map((problem, index) => (
          <Alert key={`${problem.field}-${index}`} tone="error">
            {t(problem.message.key, problem.message.values)}
          </Alert>
        ))}

        <Button
          size="lg"
          fullWidth
          onClick={onConfirm}
          isLoading={isConfirming}
          disabled={confirmDisabled}
        >
          <Receipt className="size-4" aria-hidden />
          {t('opticalOrders.confirm.action', { total: format(totalMinor) })}
        </Button>
        <p className="text-muted text-xs">{t('opticalOrders.confirm.hint')}</p>
      </div>
    </div>
  );
}
